import { useQuery } from '@apollo/client/react'

import { publicApiClient } from '~publish/clients/publicApiClient'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'

export const PublicInsightsSampleDocument = graphql(/* GraphQL */ `
  query PublicInsightsSample(
    $input: PublicInsightsSampleInput!
    $sort: PublicInsightsSampleTopPostsSortInput!
    $after: String
  ) {
    publicInsightsSample(input: $input) {
      __typename
      ... on PublicInsightsSampleSuccess {
        calculatedAt
        currentPeriod {
          start
          end
        }
        previousPeriod {
          start
          end
        }
        availableChannels {
          id
          name
          avatar
          service
        }
        allChannelsPostInsights {
          summary {
            type
            unit
            name
            description
            value
            difference
            differenceMode
          }
          channelInsights {
            channel {
              id
              name
              avatar
              service
            }
            metrics {
              type
              unit
              displayName
              description
              value
              difference
            }
          }
          channelTimeSeries {
            channel {
              id
              name
              service
            }
            firstChannelDataCollectedAt
            primary {
              followers {
                date
                value
                variation
                source
              }
              reach {
                date
                value
                variation
                source
              }
              views {
                date
                value
                variation
                source
              }
              posts {
                date
                value
                variation
                source
              }
            }
            comparison {
              followers {
                date
                value
                source
              }
              reach {
                date
                value
                source
              }
              views {
                date
                value
                source
              }
              posts {
                date
                value
                source
              }
            }
          }
        }
        topPosts(first: 5, after: $after, sort: $sort) {
          edges {
            cursor
            node {
              id
              text
              sentAt
              externalLink
              channelId
              channelService
              channel {
                id
                name
                avatar
                service
              }
              metadata {
                type
              }
              assets {
                id
                type
                thumbnail
              }
              metrics {
                type
                value
              }
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
          totalCount
        }
      }
      ... on PublicInsightsSampleUnavailable {
        message
      }
      ... on PublicInsightsSampleInvalidInput {
        message
      }
      ... on LimitReachedError {
        message
      }
    }
  }
`)

export type PublicInsightsSampleVariables = VariablesOf<
  typeof PublicInsightsSampleDocument
>
export type PublicInsightsSample = Extract<
  ResultOf<typeof PublicInsightsSampleDocument>['publicInsightsSample'],
  { __typename: 'PublicInsightsSampleSuccess' }
>

export function usePublicInsightsSample(
  variables: PublicInsightsSampleVariables,
  { skip = false }: { skip?: boolean } = {},
): useQuery.Result<
  ResultOf<typeof PublicInsightsSampleDocument>,
  PublicInsightsSampleVariables
> {
  return useQuery(PublicInsightsSampleDocument, {
    client: publicApiClient,
    variables,
    skip,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })
}
